import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, RefreshCw, Loader, Check, X as XIcon, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

const RISK = { none: ['No risk seen', 'sf-badge-success'], low: ['Low risk', 'sf-badge-neutral'], medium: ['Medium risk', 'sf-badge-warning'], high: ['High risk', 'sf-badge-error'] };
const ago = (d) => {
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};

/**
 * What the AI thinks about this record: a short summary, the best next step, any risk, and anything an agent
 * wants to do here that is waiting for a person. Hidden for plans without agents.
 */
export default function RecordAgentPanel({ entityType, entityId }) {
  const [insight, setInsight] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | hidden
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, p] = await Promise.all([api.get(`/agents/insights/${entityType}/${entityId}`), api.get('/agents/proposals', { params: { status: 'pending' } })]);
      setInsight(i.data.data);
      setProposals((p.data.data || []).filter((x) => x.identity?.entity?.id === String(entityId)));
      setState('ready');
    } catch (e) {
      setState(e?.response?.status === 403 ? 'hidden' : 'ready');
    }
  }, [entityType, entityId]);
  useEffect(() => { if (entityId) load(); }, [entityId, load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/agents/insights/${entityType}/${entityId}/refresh`, { force: true });
      setInsight(r.data.data);
    } catch (e) { alertService.error(e?.response?.data?.error || 'Could not refresh'); } finally { setBusy(false); }
  };
  const decide = async (p, action) => {
    let reason = '';
    if (action === 'reject') reason = window.prompt('Why not? (the agent will remember this)') || '';
    else if (!(await alertService.confirm('Approve this action?', p.summary, p.tier === 'write' ? 'warning' : 'danger', 'Approve'))) return;
    try {
      await api.post(`/support/approvals/${p._id}/${action}`, action === 'reject' ? { reason } : {});
      window.dispatchEvent(new Event('agents:changed'));
      load();
    } catch (e) { alertService.error(e?.response?.data?.error || 'Could not complete that'); load(); }
  };

  if (state === 'hidden') return null;
  const risk = RISK[insight?.risk?.level || 'none'];
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4" data-testid="record-agent-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-[13px] font-bold text-[#2E2E2E]"><Sparkles size={15} className="text-[#0176D3]" /> AI assistant</div>
        <div className="flex items-center gap-2">
          {insight?.generatedAt && <span className="text-[11px] text-[#706E6B]">Updated {ago(insight.generatedAt)}</span>}
          <button type="button" onClick={refresh} disabled={busy} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0176D3] disabled:opacity-50">{busy ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />} {insight ? 'Refresh' : 'Analyse'}</button>
        </div>
      </div>
      {state === 'loading' && <Loader size={16} className="animate-spin text-[#0176D3]" />}
      {state === 'ready' && !insight && <p className="text-[13px] text-[#706E6B]">No analysis yet. Press Analyse to get a summary and the best next step (uses a few AI credits).</p>}
      {insight && (
        <div className="space-y-2 text-[13px]">
          <p className="text-[#2E2E2E]">{insight.summary}</p>
          {insight.nextBestAction?.action && (
            <div className="bg-[#EAF4FE] border border-[#B8DAF3] rounded px-3 py-2"><span className="text-[11px] font-bold text-[#014486] uppercase">Next best step</span><div>{insight.nextBestAction.action}</div>{insight.nextBestAction.reason && <div className="text-[12px] text-[#706E6B]">{insight.nextBestAction.reason}</div>}</div>
          )}
          <div className="flex flex-wrap items-center gap-2"><span className={`sf-badge ${risk[1]}`}>{risk[0]}</span>{(insight.risk?.reasons || []).map((r) => <span key={r} className="text-[12px] text-[#706E6B]">{r}</span>)}</div>
        </div>
      )}
      {proposals.length > 0 && (
        <div className="mt-3 border-t border-[#F3F2F2] pt-3 space-y-2">
          <div className="flex items-center gap-1 text-[12px] font-bold text-[#B35F00]"><AlertTriangle size={13} /> Waiting for you</div>
          {proposals.map((p) => (
            <div key={p._id} className="flex items-center justify-between gap-3 text-[12px]">
              <span className="font-mono break-all">{p.summary}</span>
              <span className="whitespace-nowrap">
                <button type="button" onClick={() => decide(p, 'approve')} className="inline-flex items-center gap-1 text-white bg-[#2E844A] px-2 py-1 rounded mr-1"><Check size={12} /> Approve</button>
                <button type="button" onClick={() => decide(p, 'reject')} className="inline-flex items-center gap-1 text-[#BA0517] border border-[#BA0517] px-2 py-1 rounded"><XIcon size={12} /> Reject</button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
